import { Component, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";

import { GateCatalogService } from "../../../../../services/api/gate-catalog/gate-catalog.service";
import { Manufacturer, ManufacturerObject } from "../../../../../models/api/gateComponents/manufacturer";
import { first } from "rxjs";

@Component({
  standalone: true,
  selector: "app-gate-manufacturer",
  imports: [ButtonComponent, ReactiveFormsModule, TranslateModule, InputTextModule],
  templateUrl: "./manufacturer.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./manufacturer.component.scss",
})
export class ManufacturerComponent {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);
  private gateCatalog = inject(GateCatalogService);

  private manufacturer: Manufacturer = this.config.data.gateCatalogItem;
  private actionType: "add" | "edit" = this.config.data.actionType;

  public form = new FormGroup({
    label: new FormControl(this.manufacturer.label, Validators.required),
  });
  public requestStarted = signal(false);

  /**
   * Saves the manufacturer to the server, either creating a new one or editing an existing one.
   * If the save is successful, it will close the dialog with the updated manufacturer as the result.
   */
  public save() {
    const manufacturerValues = this.form?.value;
    if (this.form.valid && manufacturerValues && manufacturerValues.label) {
      if (!this.requestStarted()) {
        const manufacturer: ManufacturerObject = {
          label: manufacturerValues.label,
        };
        this.manufacturer.properties = manufacturer;
        this.requestStarted.set(true);
        switch (this.actionType) {
          case "add":
            this.gateCatalog
              .createGateCatalogItem(this.manufacturer)
              .pipe(first())
              .subscribe({
                next: () => {
                  this.requestStarted.set(false);
                  this.ref.close(this.manufacturer);
                },
                error: () => {
                  this.requestStarted.set(false);
                },
              });
            break;
          case "edit":
            this.gateCatalog
              .editGateCatalogItem(this.manufacturer)
              .pipe(first())
              .subscribe({
                next: () => {
                  this.requestStarted.set(false);
                  this.ref.close(this.manufacturer);
                },
                error: () => {
                  this.requestStarted.set(false);
                },
              });
            break;
        }
      }
    }
  }
  /**
   * Handles the Enter key press event.
   * If pressed, calls the save() method.
   * @param event KeyboardEvent
   */
  public onKeyUp(event: KeyboardEvent) {
    if (event.key === "Enter") {
      this.save();
    }
  }

  /**
   * Closes the dialog without returning any result.
   */
  public closeDialog() {
    this.ref.close();
  }
}
